import { motion } from "framer-motion";
import React, { ReactNode } from "react";
import { forkJoin, of, Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import ApiCacheService from "../../utilities/apiCacheService";
import { ApiEndpointResolver } from "../../utilities/apiEndpointResolver";
import ConfigPropService from "../../utilities/configPropService";
import DateService from "../../utilities/dateService";
import DigitalDataService from "../../utilities/digitalDataService";
import HttpClient from "../../utilities/httpClient";
import LoginUrlService from "../../utilities/loginUrlService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import OrderApprovalService from "../../utilities/orderApprovalService";
import ArrowIcon from "../arrowIcon/arrowIcon";
import Icon from "../icon/icon";
import Loading from "../loading/loading";
import {
  variants,
  variantsReverse,
} from "../navMenu/framerMotionSlideVariants";
import NotificationIcon from "./notificationIcon";
import OrderApproval from "./orderApproval";
// import { Orders } from "./test";
import WhereIsMyStuff from "./whereIsMyStuff";
import EnvironmentService from "../../utilities/environmentService";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

export interface INotificationProps {
  closeRight?: boolean;
  className?: string;
  setOpen: (open: boolean) => void;
  isApprover?: boolean;
  ordersAwaitingApproval: any;
  isCookied: boolean;
  preventCheckoutMessage: string;
}

interface INotificationState {
  notificationTypes: string[];
  navTokens: { [key: string]: string };
  configProps: { [key: string]: string };
  expandedOrderApproval?: string;
  [key: string]: unknown;
  loginHref: string;
}

interface INotificationTypes {
  [key: string]: INotificationType;
}

interface INotificationType {
  listTitle: string;
  iconOrSvg: string | ReactNode;
  isVisible:
    | boolean
    | ((props: INotificationProps, state: INotificationState) => boolean);
  /** use value from navigationTokens.js  */
  navTokenId: string;
  emptyState: ReactNode;
  displayEmptyState: (notificationData: INotificationData) => boolean;
  mapper: (
    element: any,
    index: number,
    notifications: Notifications,
  ) => ReactNode;
  apiEndpoint:
    | string
    | ((props: INotificationProps, state: INotificationState) => string);
  useAlreadyRetrievedData?: (
    props: INotificationProps,
    state: INotificationState,
  ) => any;
  getNotificationCount: (apiResponse: any) => number;
  getArray: (apiResponse: any) => any[];
  cacheApiCall?: boolean;
  skipCall?: () => boolean;
}

interface INotificationData {
  array: any[];
  count: number;
  loaded: boolean;
  isVisible: boolean;
}

const WheresMyStuffKey = "Where's My Stuff?";
const AwaitingApprovalKey = "Awaiting Approval";
const isEcom = EnvironmentService.get().isEcom;

const NotificationTypes: INotificationTypes = {};

NotificationTypes[WheresMyStuffKey] = {
  listTitle: "Orders",
  iconOrSvg: (
    <img
      src={process.env.PUBLIC_URL + "/images/icon-orders-24px.svg"}
      alt="Orders Awaiting Approval"
    />
  ),
  isVisible: true,
  navTokenId: NavigationTokens.dashboard,
  emptyState: (
    <div className="text-center">
      There are currently no orders scheduled for delivery.
    </div>
  ),
  mapper: (element: any, index: number, notifications: Notifications) => {
    return (
      <WhereIsMyStuff
        navTokens={notifications.state.navTokens}
        delivery={element}
        key={index}
        index={index}
      />
    );
  },
  displayEmptyState: (notificationData: INotificationData) => {
    return notificationData.array.length === 0;
  },
  apiEndpoint: (props: INotificationProps, state: INotificationState) => {
    return ApiEndpointResolver.wheresMyStuffUrl(
      state.configProps[ConfigProps.DeliveryStatusEndpoint].replace("/", "_"),
    );
  },
  getNotificationCount: () => {
    // 0 is intended until we have real delivery notifications
    return 0;
  },
  getArray: (apiResponse: any) => {
    // apiResponse = Orders;
    const orders: any[] = apiResponse.orders;
    let deliveries = [];

    // bunch of silly logic to match how dashboard displays orders
    // all of this should happen server side
    if (apiResponse.outForDelivery) {
      for (let i = 0; i < apiResponse.outForDelivery.length; i++) {
        const delivery = apiResponse.outForDelivery[i];
        delivery.order = isEcom
          ? orders[delivery.orderId]
          : orders[delivery.orderId.toLowerCase()];
        delivery.isOutForDelivery = true;
        delivery.sortPriority = -100;
        deliveries.push(delivery);
      }
    }

    if (apiResponse.delivered) {
      for (let i = 0; i < apiResponse.delivered.length; i++) {
        const delivery = apiResponse.delivered[i];
        delivery.order = isEcom
          ? orders[delivery.orderId]
          : orders[delivery.orderId.toLowerCase()];
        delivery.isDelivered = true;
        delivery.sortBackwards = true;
        delivery.sortPriority = 0;
        deliveries.push(delivery);
      }
    }

    if (apiResponse.internalToBeDelivered) {
      for (let i = 0; i < apiResponse.internalToBeDelivered.length; i++) {
        const delivery = apiResponse.internalToBeDelivered[i];
        delivery.order = isEcom
          ? orders[delivery.orderId]
          : orders[delivery.orderId.toLowerCase()];
        delivery.isToBeDelivered = true;
        delivery.sortPriority = 100;
        deliveries.push(delivery);
      }
    }

    if (apiResponse.thirdPartyDelivery) {
      for (let i = 0; i < apiResponse.thirdPartyDelivery.length; i++) {
        const delivery = apiResponse.thirdPartyDelivery[i];
        delivery.order = isEcom
          ? orders[delivery.orderId]
          : orders[delivery.orderId.toLowerCase()];
        delivery.isThirdPartyDelivery = true;
        delivery.sortPriority = 200;
        deliveries.push(delivery);
      }
    }

    deliveries = deliveries.sort((lhs, rhs) => {
      if (lhs.sortPriority < rhs.sortPriority) {
        return -1;
      } else if (lhs.sortPriority > rhs.sortPriority) {
        return 1;
      }

      const lhsDate = DateService.parse(
        lhs.isToBeDelivered ? lhs.deliveryInfo.manifestDate : lhs.order.date,
      );
      const rhsDate = DateService.parse(
        rhs.isToBeDelivered ? rhs.deliveryInfo.manifestDate : rhs.order.date,
      );

      let modifier = 1;
      if (lhs.sortPriority === rhs.sortPriority) {
        if (lhs.sortBackwards) {
          modifier = -1;
        }
      }

      if (lhsDate.getTime() < rhsDate.getTime()) {
        return modifier * 1;
      }

      if (lhsDate.getTime() > rhsDate.getTime()) {
        return modifier * -1;
      }

      return 0;
    });

    return deliveries;
  },
  cacheApiCall: true,
  skipCall: () => {
    return !DigitalDataService.userConfigId();
  },
};

NotificationTypes[AwaitingApprovalKey] = {
  listTitle: "Approvals",
  iconOrSvg: "thumb_up",
  isVisible: (props, state) => {
    return !!props.isApprover;
  },
  navTokenId: NavigationTokens.OrdersAwaitingApproval,
  emptyState: (
    <div className="text-center">
      There are currently no orders awaiting approval.
    </div>
  ),
  displayEmptyState: (notificationData: INotificationData) => {
    // since orders animate after approved/rejected, they aren't removed immediately
    // have to check if all orders are completed to know if it should show empty state
    return (
      notificationData.array.length === 0 ||
      notificationData.array.filter(
        (o) =>
          !OrderApprovalService.isCompleted(
            o.awaitingOrderIdentifier.approvalGuid,
          ),
      ).length === 0
    );
  },
  mapper: (element: any, index: number, notifications: Notifications) => {
    return (
      <OrderApproval
        approval={element}
        key={element.awaitingOrderIdentifier.approvalGuid}
        navTokens={notifications.state.navTokens}
        expandedOrderApproval={notifications.state.expandedOrderApproval}
        expand={notifications.toggleOrderApproval}
        isCookied={notifications.props.isCookied}
        preventCheckoutMessage={notifications.props.preventCheckoutMessage}
        loginHref={notifications.state.loginHref}
      />
    );
  },
  apiEndpoint: ApiEndpointResolver.getOrderApprovalsUrl(),
  useAlreadyRetrievedData: (
    props: INotificationProps,
    state: INotificationState,
  ) => {
    return props.ordersAwaitingApproval;
  },
  getArray: (apiResponse: any) => {
    return apiResponse.orders;
  },
  getNotificationCount: (apiResponse: any) => {
    return apiResponse.notificationCount;
  },
};

class Notifications extends React.Component<
  INotificationProps,
  INotificationState
> {
  private navTokenSub: Subscription | null = null;
  private configPropSub: Subscription | null = null;
  private processNotificationsSub: Subscription | null = null;
  private notificationTypeSubs: Subscription[] = [];
  private loginUrlSub: Subscription | null = null;
  private navTokensResolved: boolean = false;

  constructor(props: INotificationProps) {
    super(props);

    const initState: INotificationState = {
      notificationTypes: Object.keys(NotificationTypes),
      navTokens: {},
      configProps: {},
      loginHref: "",
    };

    // dynamically track state for each notification type
    for (let i = 0; i < initState.notificationTypes.length; i++) {
      const notificationData: INotificationData = {
        array: [],
        count: 0,
        loaded: false,
        isVisible: false,
      };

      initState[initState.notificationTypes[i]] = notificationData;
    }

    this.state = initState;

    this.toggleOrderApproval = this.toggleOrderApproval.bind(this);
  }

  static getDerivedStateFromProps(
    props: INotificationProps,
    state: INotificationState,
  ) {
    // since order approvals aren't managed by this component directly,
    // we have to derive state from props
    const oaaString = JSON.stringify(props.ordersAwaitingApproval);
    if (
      oaaString === state.ordersAwaitingApproval &&
      props.isApprover === state.isApprover
    ) {
      // order approval props haven't changed. Do nothing
      return null;
    }

    const definition = NotificationTypes[AwaitingApprovalKey];

    let newState = {
      ordersAwaitingApproval: oaaString,
      isApprover: props.isApprover,
    };

    if (definition.useAlreadyRetrievedData) {
      const apiResponse = definition.useAlreadyRetrievedData(props, state);

      newState = {
        ...Notifications.processApiResponse(
          AwaitingApprovalKey,
          apiResponse,
          props,
          state,
        ),
        ...newState,
      };
    } else if (typeof definition.isVisible !== "boolean") {
      const currentState = (state as Record<string, unknown>)[
        AwaitingApprovalKey
      ] as INotificationData;

      const isVisible = definition.isVisible(props, state);

      if (currentState.isVisible !== isVisible) {
        currentState.isVisible = isVisible;

        const tempState: Record<string, unknown> = {};
        tempState[AwaitingApprovalKey] = currentState;

        newState = {
          ...tempState,
          ...newState,
        };
      }
    }

    return newState;
  }

  static processApiResponse(
    notificationType: string,
    apiResponse: any,
    props: INotificationProps,
    state: INotificationState,
  ): any {
    const definition: INotificationType = NotificationTypes[notificationType];

    const array = definition.getArray(apiResponse);

    const notificationData: INotificationData = {
      count: definition.getNotificationCount(apiResponse),
      array: array ? array : [],
      loaded: array ? true : false,
      isVisible:
        typeof definition.isVisible === "boolean"
          ? definition.isVisible
          : definition.isVisible(props, state),
    };

    const newState: any = {};
    newState[notificationType] = notificationData;

    return newState;
  }

  toggleOrderApproval(id: string | undefined) {
    if (this.state.expandedOrderApproval === id) {
      id = undefined;
    }

    this.setState({
      expandedOrderApproval: id,
    });
  }

  componentDidMount() {
    const navTokensToRequest = this.state.notificationTypes.map(
      (notificationType) => NotificationTypes[notificationType].navTokenId,
    );
    // needed for child components
    navTokensToRequest.push(
      NavigationTokens.OrderAwaitingApprovalDetails,
      NavigationTokens.OrderHistoryDetails,
      NavigationTokens.orderHistory,
    );

    const navTokensRequest = NavigationTokensService.get(navTokensToRequest);

    this.navTokenSub = navTokensRequest.subscribe((navTokens: any) => {
      // do nothing?
    });

    const configPropsRequest = ConfigPropService.get([
      ConfigProps.DeliveryStatusEndpoint,
      ConfigProps.EnableDashboard,
      ConfigProps.DisplayOrderHistoryLink,
      ConfigProps.EnableResponsiveAccountDashboard,
    ]);

    this.configPropSub = configPropsRequest.subscribe((configProps: any) => {
      // do nothing?
    });

    const loginUrlRequest = LoginUrlService.get();

    this.loginUrlSub = navTokensRequest.subscribe((loginUrl: any) => {
      // do nothing?
    });

    this.processNotificationsSub = forkJoin([
      navTokensRequest,
      configPropsRequest,
      loginUrlRequest,
    ]).subscribe((responses: any[]) => {
      this.navTokensResolved = true;
      // we have all config data, start loading notification data
      this.setState(
        {
          navTokens: responses[0],
          configProps: responses[1],
          loginHref: responses[2],
        },
        () => {
          for (let i = 0; i < this.state.notificationTypes.length; i++) {
            const notificationType: string = this.state.notificationTypes[i];
            const definition: INotificationType =
              NotificationTypes[notificationType];

            const currentState: INotificationData = this.state[
              notificationType
            ] as INotificationData;

            currentState.isVisible =
              typeof definition.isVisible === "boolean"
                ? definition.isVisible
                : definition.isVisible(this.props, this.state);

            const newState: Partial<INotificationState> = {};
            newState[notificationType] = currentState;

            this.setState(newState, () => {
              if (currentState.isVisible) {
                if (!definition.useAlreadyRetrievedData) {
                  const apiEndpoint =
                    typeof definition.apiEndpoint === "string"
                      ? definition.apiEndpoint
                      : definition.apiEndpoint(this.props, this.state);

                  const skip = definition.skipCall
                    ? definition.skipCall()
                    : false;

                  let apiObservable;
                  if (skip) {
                    apiObservable = of({});
                  } else {
                    const httpRequest = HttpClient.get({
                      baseUri: apiEndpoint,
                    });
                    apiObservable = definition.cacheApiCall
                      ? ApiCacheService.withCache(apiEndpoint, httpRequest)
                      : httpRequest;
                  }

                  const sub = apiObservable.subscribe((apiResponse: any) => {
                    this.setState(
                      Notifications.processApiResponse(
                        notificationType,
                        apiResponse,
                        this.props,
                        this.state,
                      ),
                    );
                  });

                  this.notificationTypeSubs.push(sub);
                }
              }
            });
          }
        },
      );
    });
  }

  componentWillUnmount() {
    if (this.navTokenSub) {
      this.navTokenSub.unsubscribe();
    }

    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.processNotificationsSub) {
      this.processNotificationsSub.unsubscribe();
    }

    if (this.notificationTypeSubs.length !== 0) {
      for (let i = 0; i < this.notificationTypeSubs.length; i++) {
        this.notificationTypeSubs[i].unsubscribe();
      }
    }

    if (this.loginUrlSub) {
      this.loginUrlSub.unsubscribe();
    }
  }

  isTypeVisible(notificationType: INotificationType) {
    if (typeof notificationType.isVisible === "boolean") {
      return notificationType.isVisible;
    }

    return notificationType.isVisible(this.props, this.state);
  }

  processIconOrSvg(notificationType: INotificationType) {
    return typeof notificationType.iconOrSvg === "string" ? (
      <Icon className="notification-icon-margin">
        {notificationType.iconOrSvg}
      </Icon>
    ) : (
      <span className="use-stroke notification-icon-margin">
        {notificationType.iconOrSvg}
      </span>
    );
  }

  render() {
    return (
      <motion.div
        initial="closed"
        animate="open"
        exit="closed"
        variants={this.props.closeRight ? variantsReverse() : variants()}
        className={`notifications${
          this.props.className ? " " + this.props.className : ""
        }${this.props.closeRight ? " right-side" : ""}${
          this.navTokensResolved ? "" : " inactive"
        }`}
        key="navmenu"
      >
        <div className="top-bar">
          <button
            className="close-button"
            onClick={() => {
              this.props.setOpen(false);
            }}
          >
            <ArrowIcon useCloseIcon={true}></ArrowIcon>
          </button>
          <Icon className="notification-icon-margin">notifications</Icon>
          <span>Notifications</span>
        </div>
        {!DigitalDataService.userConfigId() && (
          <div className="notifications-anon-container">
            <div className="font-weight-bold mb-2 text-align-center">
              Quickly View Your Order Status
            </div>
            <div className="mb-4">
              Sign in or register to view orders scheduled for delivery.
            </div>
            <a
              className="btn btn-lg btn-primary font-weight-bold mb-2"
              href={this.state.loginHref}
              role="button"
              type="button"
            >
              SIGN IN OR REGISTER
            </a>
          </div>
        )}
        {DigitalDataService.userConfigId() && (
          <div className="container mt-20">
            {this.state.notificationTypes.map((notificationType) => {
              const aTagProps: React.AnchorHTMLAttributes<HTMLAnchorElement> =
                {};

              if (
                this.state.navTokens &&
                this.state.navTokens[
                  NotificationTypes[notificationType].navTokenId
                ]
              ) {
                if (notificationType === WheresMyStuffKey) {
                  if (
                    !ConfigPropService.isYesOrTrue(
                      this.state.configProps[ConfigProps.EnableDashboard],
                    ) &&
                    !ConfigPropService.isYesOrTrue(
                      this.state.configProps[
                        ConfigProps.DisplayOrderHistoryLink
                      ],
                    )
                  ) {
                    aTagProps.className = "d-none";
                  }

                  const enableResponsiveAccountDashboard =
                    ConfigPropService.isYesOrTrue(
                      this.state.configProps[
                        ConfigProps.EnableResponsiveAccountDashboard
                      ],
                    );

                  const accountDashboardUrl =
                    WBMUrlService.getAccountDashboardUrl(
                      BaseHostUrlService.get(),
                      `${
                        enableResponsiveAccountDashboard
                          ? EnvironmentService.get().ecomVnextUrl
                          : EnvironmentService.get().ecom
                      }`,
                      enableResponsiveAccountDashboard,
                    );

                  aTagProps.href = ConfigPropService.isYesOrTrue(
                    this.state.configProps[ConfigProps.EnableDashboard],
                  )
                    ? accountDashboardUrl
                    : this.state.navTokens[NavigationTokens.orderHistory];
                } else {
                  aTagProps.href =
                    this.state.navTokens[
                      NotificationTypes[notificationType].navTokenId
                    ];
                }
              }

              return (
                <React.Fragment key={notificationType}>
                  {this.isTypeVisible(NotificationTypes[notificationType]) && (
                    <div className="form-group">
                      <div className="notification-header">
                        {this.processIconOrSvg(
                          NotificationTypes[notificationType],
                        )}

                        <b>{notificationType}</b>

                        {(this.state[notificationType] as INotificationData)
                          .count !== 0 && (
                          <NotificationIcon left={0}></NotificationIcon>
                        )}

                        <a className="notification-go-to" {...aTagProps}>
                          All {NotificationTypes[notificationType].listTitle}
                        </a>
                      </div>

                      <div className="notification-container">
                        {(this.state[notificationType] as INotificationData)
                          .loaded &&
                          NotificationTypes[notificationType].displayEmptyState(
                            this.state[notificationType] as INotificationData,
                          ) && (
                            <div className="pt-2 pb-2">
                              {NotificationTypes[notificationType].emptyState}
                            </div>
                          )}

                        {!(this.state[notificationType] as INotificationData)
                          .loaded && <Loading />}

                        {(this.state[notificationType] as INotificationData)
                          .loaded &&
                          (this.state[notificationType] as INotificationData)
                            .array.length !== 0 && (
                            <div className="notifications-wrapper">
                              {(
                                this.state[
                                  notificationType
                                ] as INotificationData
                              ).array.map((element: any, index: number) =>
                                NotificationTypes[notificationType].mapper(
                                  element,
                                  index,
                                  this,
                                ),
                              )}
                            </div>
                          )}

                        {(this.state[notificationType] as INotificationData)
                          .loaded &&
                          (this.state[notificationType] as INotificationData)
                            .count !== 0 && (
                            <a
                              className="notification-go-to small center form-group pt-2 pb-2"
                              {...aTagProps}
                            >
                              See All{" "}
                              <b>
                                {
                                  (
                                    this.state[
                                      notificationType
                                    ] as INotificationData
                                  ).count
                                }
                              </b>{" "}
                              {NotificationTypes[notificationType].listTitle}
                            </a>
                          )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </motion.div>
    );
  }
}

export default Notifications;
